import { GetDevicesInGeofencesQuery } from "graphqlBase/Devices/__generated__/getDevicesInGeofences";
import { GetAllStorageAreasQuery } from "graphqlBase/StorageAreas/__generated__/getAllStorageAreas";
import L, { LatLngExpression } from "leaflet";
import memoize from "lodash/memoize";

import { useCallback, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { DeviceInGeofence, FlattenData, StorageAreaTypes } from "..";
import { selectedStorageAreaIdsAtom } from "../recoilBox";
interface DataProps {
  allStorageAreas?: GetAllStorageAreasQuery | null;
  devicesInGeofencesPre: GetDevicesInGeofencesQuery | undefined;
}

const useGetCalculatedData = ({ allStorageAreas, devicesInGeofencesPre }: DataProps) => {
  const storageAreaIds = useRecoilValue(selectedStorageAreaIdsAtom);

  const filteredStorageAreas = useMemo(() => {
    return allStorageAreas?.storageAreas?.items?.filter((storageArea) => storageAreaIds.includes(storageArea.id)) ?? [];
  }, [allStorageAreas?.storageAreas, storageAreaIds]);

  const devicesWithAssets = useMemo(() => {
    if (!storageAreaIds.length) return [];
    const devicesInGeofences = (devicesInGeofencesPre?.queryDeviceWithinGeofence?.geofenceDevices ??
      ([] as unknown)) as DeviceInGeofence[];

    const seenIds = new Set<string | undefined>();
    return devicesInGeofences.reduce<DeviceInGeofence[]>((acc, current) => {
      if (!current?.device?.assetDevices?.items || !current?.device?.assetDevices?.items?.[0]?.asset.assetMasterData)
        return acc;
      if (seenIds.has(current.device?.id)) return acc;
      seenIds.add(current.device?.id);
      return acc.concat({ ...current, id: current.device.assetDevices?.items?.[0]?.asset?.id });
    }, []);
  }, [devicesInGeofencesPre, storageAreaIds]);

  const devicesByStorageAreaId = useMemo(() => {
    // Track seen device IDs globally so each device appears in at most one storage area.
    const seenDeviceIds = new Set<string | undefined>();
    return devicesWithAssets.reduce<{ [key: string]: DeviceInGeofence[] }>((acc, device) => {
      if (seenDeviceIds.has(device.device?.id)) return acc;
      const matchingStorageArea = (allStorageAreas?.storageAreas?.items ?? []).find((storageArea) =>
        L.polygon(storageArea?.geofence?.coordinates as L.LatLngExpression[][])
          .getBounds()
          .contains(device.location?.coordinates as LatLngExpression)
      );
      if (!matchingStorageArea) return acc;
      seenDeviceIds.add(device.device?.id);
      return { ...acc, [matchingStorageArea.id]: (acc[matchingStorageArea.id] ?? []).concat(device) };
    }, {});
  }, [allStorageAreas?.storageAreas, devicesWithAssets]);

  const getFlattenData = useCallback(
    memoize((data: StorageAreaTypes[]): FlattenData[] => {
      let storageArea: any = [];
      return data
        .map((m) => {
          if (m.storageArea && m.storageArea.length) {
            storageArea = [...storageArea, ...m.storageArea];
          }

          return m;
        })
        .concat(storageArea.length ? getFlattenData(storageArea) : storageArea);
    }),
    []
  );

  return { filteredStorageAreas, devicesWithAssets, devicesByStorageAreaId, getFlattenData };
};
export default useGetCalculatedData;
